import { computed, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { CouponState, type Coupon, type CouponResponse, type Plan, CouponDurationType, CouponDiscountType } from '@/types'
import { useLazyQuery } from '@vue/apollo-composable'
import { fetchCoupon as fetchCouponQuery, listCoupons } from '@/queries'
import { usePlanStore } from '@/store/PlanStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useErrorsStore } from '@/store/ErrorsStore'

export const useCouponStore = defineStore('CouponStore', () => {
  const checkoutStore = useCheckoutStore()
  const errorsStore = useErrorsStore()
  const planStore = usePlanStore()

  const { plans: allPlans } = storeToRefs(planStore)
  const { setCheckoutProperty } = checkoutStore
  const { setAppError } = errorsStore

  const coupon = ref<Coupon | null>(null)
  const coupons = ref<Coupon[]>([])
  const couponCode = ref('')
  const isLoading = ref(false)

  const setCouponCode = (code: string) => (couponCode.value = code)

  const { onResult: onFetchCouponResult, load: fetchCouponInitial } = useLazyQuery(fetchCouponQuery)

  const fetchCoupon = (couponCode: string) => fetchCouponInitial(fetchCouponQuery, { couponCode: `code-${couponCode}` })

  /* Fetch all coupons */
  const { onResult: onFetchCouponsResult, load: fetchCoupons } = useLazyQuery(
    listCoupons,
    { state: 'active' },
    {
      errorPolicy: 'ignore',
      fetchPolicy: 'cache-first',
      context: {
        fetchOptions: {
          method: 'GET'
        }
      }
    }
  )

  const handleCouponCode = (couponCode: string) => {
    const couponToApply = coupons.value?.find(coupon => coupon.code === couponCode)

    if (couponToApply?.state === CouponState.Redeemable) {
      coupon.value = couponToApply
    } else {
      removeCoupon()
    }
  }

  onFetchCouponResult(result => {
    if (!result.data?.coupon) {
      return
    }

    const { coupon: couponResponse } = result.data

    const state = couponResponse?.uniqueCouponCode?.state || couponResponse?.state

    const couponCanBeApplied = !!(couponResponse?.code && state === CouponState.Redeemable && !couponResponse?.error)

    if (!couponCanBeApplied) {
      setAppError({
        title: 'Coupon Code is not valid',
        message: "Your coupon code has expired or doesn't exist."
      })

      removeCoupon()

      return
    }
    // Can be applied, so reduce it
    const couponToApply = reduceCoupon(couponResponse)
    coupon.value = couponToApply
  })

  onFetchCouponsResult(result => {
    if (!result.data?.listCoupons) {
      return
    }

    const {
      data: { listCoupons }
    } = result

    const reducedCoupons = reduceCoupons(listCoupons.data)

    coupons.value = reducedCoupons
  })

  const removeCoupon = () => {
    setCheckoutProperty({
      couponCodes: []
    })
    coupon.value = null
    couponCode.value = ''
  }

  const applyCouponToCheckout = (couponCode: string) => {
    setCheckoutProperty({
      couponCodes: [couponCode]
    })
  }

  // Checks if coupon is valid in the current context (plan/product/user/etc)
  const checkCouponCanBeAppliedToPlan = (coupon: Coupon, planCode: string) => {
    const { plans, appliesToAllPlans } = coupon

    const canBeAppliedToPlan = !!plans?.find((item: Plan) => item.code === planCode)

    if (!appliesToAllPlans && !canBeAppliedToPlan) {
      setAppError({
        title: 'Coupon code is not applicable',
        message: 'Your coupon code is not applicable to this plan.'
      })

      removeCoupon()
    }
  }

  const reduceCoupons = (coupons: CouponResponse[]): Coupon[] => coupons.map(coupon => reduceCoupon(coupon))

  const reduceCoupon = (couponResponse: CouponResponse): Coupon => {
    const {
      id,
      state: responseState,
      code: responseCode,
      duration,
      temporalAmount,
      temporalUnit,
      name,
      appliesToAllPlans,
      plans,
      discount,
      uniqueCouponCode
    } = couponResponse
    const { percent, trial, currencies, type } = discount

    const planResults = plans
      ? (() => {
          const planMap = new Map(allPlans.value.map((plan: Plan) => [plan.code, plan]))
          return plans.map(planResult => planMap.get(planResult.code)).filter((plan): plan is Plan => plan !== undefined)
        })()
      : []

    // use the unique code if there is one
    const code = uniqueCouponCode ? uniqueCouponCode.code : responseCode
    const state = uniqueCouponCode ? uniqueCouponCode.state : responseState
    return {
      id,
      state,
      code,
      duration,
      temporalAmount,
      temporalUnit,
      name,
      appliesToAllPlans,
      plans: planResults,
      percent,
      trial,
      currencies,
      discountType: type
    }
  }

  const eligiblePlans = computed(() => coupon.value?.plans)
  const isRedeemable = computed(() => coupon.value?.state === CouponState.Redeemable)
  const appliedCouponCode = computed(() => coupon.value?.code)
  const hasAppliedCoupon = computed(() => !!appliedCouponCode.value)
  const redeemableCoupons = computed(() => coupons.value.filter(coupon => coupon.state === CouponState.Redeemable))
  const codeIsRedeemable = (code: string) => redeemableCoupons.value.find(coupon => coupon.code === code)
  const appliedCouponPercent = computed(() => coupon.value?.percent)
  const appliedCouponCurrencies = computed(() => coupon.value?.currencies)
  const appliedCouponTrial = computed(() => coupon.value?.trial)
  const appliedCouponTrialLength = computed(() => `${appliedCouponTrial.value?.length} ${appliedCouponTrial.value?.unit}`)
  const appliedCouponTrialLengthPlural = computed(() => `${appliedCouponTrial.value?.length} ${appliedCouponTrial.value?.unit}s`)
  const couponTemporalAmount = computed(() => coupon.value?.temporalAmount)
  const couponTemporalUnit = computed(() => coupon.value?.temporalUnit)
  const couponTemporalUnitPlural = computed(() =>
    couponTemporalAmount.value && couponTemporalAmount.value > 1 ? `${couponTemporalUnit.value}s` : couponTemporalUnit.value
  )
  const couponDuration = computed(() => `${couponTemporalAmount.value} ${couponTemporalUnitPlural.value}`)
  const couponDiscountType = computed(() => coupon.value?.discountType)
  const couponDurationType = computed(() => coupon.value?.duration)

  const isDiscountCoupon = computed(() => couponDiscountType.value === CouponDiscountType.Percent || couponDiscountType.value === CouponDiscountType.Fixed)
  const isFreeTrialCoupon = computed(() => couponDiscountType.value === CouponDiscountType.Trial)

  const isTemporalCoupon = computed(() => couponDurationType.value === CouponDurationType.Temporal)
  const isForeverCoupon = computed(() => couponDurationType.value === CouponDurationType.Forever)
  const isSingleUseDiscountCoupon = computed(() => isDiscountCoupon.value && couponDurationType.value === CouponDurationType.SingleUse)

  return {
    appliedCouponCode,
    appliedCouponCurrencies,
    appliedCouponPercent,
    appliedCouponTrial,
    appliedCouponTrialLength,
    appliedCouponTrialLengthPlural,
    applyCouponToCheckout,
    checkCouponCanBeAppliedToPlan,
    codeIsRedeemable,
    coupon,
    couponCode,
    coupons,
    couponDuration,
    couponDiscountType,
    couponDurationType,
    eligiblePlans,
    fetchCoupon,
    fetchCoupons,
    handleCouponCode,
    hasAppliedCoupon,
    isDiscountCoupon,
    isTemporalCoupon,
    isForeverCoupon,
    isFreeTrialCoupon,
    isSingleUseDiscountCoupon,
    isLoading,
    isRedeemable,
    redeemableCoupons,
    removeCoupon,
    setCouponCode
  }
})
